// =============================================================================
// Module Project - Repository: Spaces
// Gestion des espaces (dossiers) pour organiser les projets
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import {
  getCurrentProjectAccessContext,
  getParentSpaceIdsForExplicitProjectShares,
  getSharedSpaceIds,
} from './projectAccessContextRepository';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
export interface Space {
  id: string;
  org_id: string;
  name: string;
  color: string | null;
  icon: string | null;
  position: number;
  is_default: boolean;
  is_archive: boolean;
  visibility: 'public' | 'private';
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

export interface SpaceCreateInput {
  org_id: string;
  name: string;
  color?: string;
  icon?: string;
  created_by?: string;
}

export interface SpaceUpdateInput {
  name?: string;
  color?: string;
  icon?: string;
  position?: number;
  visibility?: 'public' | 'private';
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH SPACES
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchSpaces(orgId: string): Promise<Space[]> {
  const { data, error } = await supabase
    .from('project_spaces')
    .select('*')
    .eq('org_id', orgId)
    .order('position')
    .order('name');

  if (error) throw error;

  const allSpaces = (data || []) as Space[];
  const accessContext = await getCurrentProjectAccessContext(orgId);

  let spaces = allSpaces;
  if (!accessContext.memberId) {
    spaces = [];
  } else if (!accessContext.isAdmin && accessContext.isGuest) {
    const [sharedSpaceIds, parentSpaceIds] = await Promise.all([
      getSharedSpaceIds(accessContext.memberId, allSpaces.map((space) => space.id)),
      getParentSpaceIdsForExplicitProjectShares(accessContext.memberId),
    ]);

    const visibleSpaceIds = new Set([...sharedSpaceIds, ...parentSpaceIds]);
    spaces = allSpaces.filter((space) => visibleSpaceIds.has(space.id));
  }
  
  // Sort: Archives always last
  return spaces.sort((a, b) => {
    if (a.is_archive && !b.is_archive) return 1;
    if (!a.is_archive && b.is_archive) return -1;
    return (a.position ?? 0) - (b.position ?? 0);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE SPACE
// ─────────────────────────────────────────────────────────────────────────────
export async function createSpace(input: SpaceCreateInput): Promise<Space> {
  const { data, error } = await supabase.rpc('create_space', {
    p_org_id: input.org_id,
    p_name: input.name,
    p_color: input.color || '#6366f1',
    p_icon: input.icon || 'folder',
    p_created_by: input.created_by || null,
  });

  if (error) throw error;
  return data as unknown as Space;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE SPACE
// ─────────────────────────────────────────────────────────────────────────────
export async function updateSpace(
  spaceId: string,
  updates: SpaceUpdateInput
): Promise<Space> {
  const { data, error } = await supabase
    .from('project_spaces')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', spaceId)
    .select()
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error("Vous n'avez pas la permission de modifier cet espace");
  return data as Space;
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE SPACE (only regular spaces can be deleted, not default or archive)
// ─────────────────────────────────────────────────────────────────────────────
export async function deleteSpace(spaceId: string): Promise<void> {
  // First check if it's a protected space
  const { data: space } = await supabase
    .from('project_spaces')
    .select('is_default, is_archive')
    .eq('id', spaceId)
    .single();

  if (space?.is_default) {
    throw new Error("Impossible de supprimer l'espace par défaut");
  }

  if (space?.is_archive) {
    throw new Error("Impossible de supprimer l'espace d'archives");
  }

  const { error } = await supabase
    .from('project_spaces')
    .delete()
    .eq('id', spaceId);

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// ASSIGN PROJECT TO SPACE
// ─────────────────────────────────────────────────────────────────────────────
export async function assignProjectToSpace(
  projectId: string,
  spaceId: string | null
): Promise<void> {
  const { error } = await supabase
    .from('project_projects')
    .update({ space_id: spaceId })
    .eq('id', projectId);

  if (error) throw error;
}
